import { useState, useEffect, useRef } from 'react';

import CreateButton from 'components/buttons/CreateButton';
import { FaHeart } from 'react-icons/fa';
import { IoMdBasket } from 'react-icons/io';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

function ProductCard(props: {
    name: string;
    img: string;
    unity: string;
    type: string;
    id: string;
    alert: number;
    price: number;
    onAddProduct: any;
    quantity: number;
    remove: any;
    onQuantityChange: any;
    order: any;
    orderedCount: number;
    imageUrl: string;
    imageUrls?: string[];
    onClick: any;
    subUnitQuantity?: number;
    hidePricesOnShop?: boolean;
    subUnity?: string;
    packagings?: any[];
    selectedPackaging?: any;
    onPackagingChange?: (packaging: any) => void;
    isLiked?: boolean;
    onToggleLike?: (productId: string) => void;
}) {
    const {
        name,
        img,
        unity,
        id,
        price,
        onAddProduct,
        quantity,
        onQuantityChange,
        order,
        orderedCount,
        imageUrl,
        imageUrls,
        onClick,
        subUnitQuantity,
        subUnity,
        hidePricesOnShop = false,
        packagings,
        selectedPackaging,
        onPackagingChange,
        isLiked = false,
        onToggleLike,
    } = props;
    const [toggleState, setToggleState] = useState('normal');
    const [newQuantity, setNewQuantity] = useState(quantity ? quantity : 1);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);

    // If some packagings are b2bOnly, restrict display to those and hide the base unit
    const b2bOnlyPackagings = packagings ? packagings.filter((p: any) => p.b2bOnly) : [];
    const effectivePackagings = b2bOnlyPackagings.length > 0 ? b2bOnlyPackagings : (packagings || []);
    const hideBaseUnit = b2bOnlyPackagings.length > 0;

    // When b2bOnly exists and nothing selected yet, resolve the default packaging for display
    const autoB2bPackaging = hideBaseUnit && !selectedPackaging
        ? (b2bOnlyPackagings.find((p: any) => p.isDefault) || b2bOnlyPackagings[0])
        : null;
    const resolvedPackaging = selectedPackaging || autoB2bPackaging;
    
    const allImages = (() => {
        // If imageUrls exists and is not empty
        if (imageUrls && Array.isArray(imageUrls) && imageUrls.length > 0) {
            return imageUrls;
        }
        // Otherwise, if imageUrl exists (single string)
        if (imageUrl && typeof imageUrl === 'string') {
            return [imageUrl];
        }
        // Fallback
        return [img];
    })();

    const changeToogleState = (state: string) => {
        setToggleState(state);
    };

    const updateQuantity = (qty: any) => {
        qty = parseInt(qty);
        Number.isInteger(qty) ? setNewQuantity(qty) : setNewQuantity(0);
    };

    const handleSubmit = async () => {
        if (!quantity) {
            onAddProduct(id, name, newQuantity);
        } else {
            onQuantityChange(newQuantity, id);
            if (newQuantity === 0) {
                setToggleState('normal');
                setNewQuantity(1);
            }
        }
    };

    const displayName = (name: string): string => {
        if (!name) return '';
        name = name.toLocaleLowerCase();
        name = name[0].toUpperCase() + name.slice(1);
        return name.length > 40 ? name.substring(0, 40) + '...' : name;
    };

    const goToPrevious = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => 
            prev === 0 ? allImages.length - 1 : prev - 1
        );
    };

    const goToNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => 
            prev === allImages.length - 1 ? 0 : prev + 1
        );
    };


    const scrollToMobileIndex = (to: number) => {
               if (!scrollRef.current) return;
               const viewport = scrollRef.current.clientWidth;
               scrollRef.current.scrollTo({ left: viewport * to, behavior: 'smooth' });
               setCurrentImageIndex(to);
           };
           const goToNextMobile = (e: React.MouseEvent) => {
               e.stopPropagation();
               const next = currentImageIndex === allImages.length - 1 ? 0 : currentImageIndex + 1;
               scrollToMobileIndex(next);
           };

    const scrollToImage = (index: number) => {
        setCurrentImageIndex(index);
        if (scrollRef.current) {
            const scrollWidth = scrollRef.current.scrollWidth;
            const clientWidth = scrollRef.current.clientWidth;
            const scrollLeft = (scrollWidth / allImages.length) * index - (clientWidth / 2);
            scrollRef.current.scrollTo({
                left: scrollLeft,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        const exist = order.items.find((i: any) => i.productId === id);

        if (!exist && toggleState === 'flipped') {
            setToggleState('normal');
            setNewQuantity(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order]);
    
    useEffect(() => {
        if (quantity && toggleState === 'normal') {
            setNewQuantity(quantity);
        }
    }, [quantity, toggleState]);

    return (
        <div
            className={`items-center rounded ${
                quantity > 0
                    ? 'bg-toola-400/5 border border-toola-400'
                    : 'bg-white'
            } py-[10px] shadow-3xl shadow-shadow-500`}
            key={id}
        >
            <div className="grid grid-cols-12 gap-3 relative">
                {orderedCount > 0 && toggleState === 'normal' && (
                    <div className="flex absolute -top-1.5 left-2 text-xs font-bold">
                        <FaHeart className="text-pink-500 my-auto" />
                        <span className="px-1 my-auto">Déjà commandé</span>
                    </div>
                )}
                {/* Like button */}
                {toggleState === 'normal' && onToggleLike && (
                    <button
                        className="absolute top-1 right-2 z-10 p-1 rounded-full transition-colors hover:bg-pink-50"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleLike(id);
                        }}
                        title={isLiked ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                    >
                        <FaHeart
                            size={16}
                            className={isLiked ? 'text-pink-500' : 'text-gray-300'}
                        />
                    </button>
                )}
                {quantity > 0 && toggleState === 'normal' && (
                    <div className="flex absolute bottom-0 right-2 text-sm text-toola-400">
                        <IoMdBasket size={15} className="flex my-auto" />
                        <span className="px-1 my-auto">
                            {quantity} {unity}
                        </span>
                    </div>
                )}
                {toggleState === 'normal' ? (
                    <div
                        className="col-span-12 flex flex-col hover:cursor-pointer mt-3 sm:mt-0"
                        onClick={onClick}
                    >
                        {/* Image Gallery */}
                        <div className="relative group">
                            <div className="mb-[2px] h-[140px] flex items-center justify-center overflow-hidden">
                                {/* Desktop: Show main image with navigation */}
                                <div className="hidden sm:block relative w-full h-full">
                                    <img
                                        className="max-h-[140px] max-w-[180px] rounded-lg object-cover mx-auto"
                                        src={allImages[currentImageIndex]}
                                        alt={name}
                                        onError={(e: any) => {
                                            e.target.src = img; // Fallback to default img
                                        }}
                                    />
                                    
                                    {/* Navigation arrows - only show if multiple images */}
                                    {allImages.length > 1 && (
                                        <>
                                            <button
                                                onClick={goToPrevious}
                                                className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                            >
                                                <MdChevronLeft className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={goToNext}
                                                className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                            >
                                                <MdChevronRight className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </div>
                                {/* Mobile: Horizontal scroll gallery */}
                                <div className="sm:hidden relative w-full h-full">
                                    {allImages.length <= 1 ? (
                                        
                                        <div className="flex items-center justify-center w-full h-full overflow-hidden">
                                            <img
                                                className="max-h-[140px] max-w-[180px] rounded-lg object-contain"
                                                src={allImages[0]}
                                                alt={name}
                                                onError={(e: any) => {
                                                    e.target.src = img;
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <>

                                            <div
                                                ref={scrollRef}
                                                className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                                                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollBehavior: 'smooth' as any }}
                                            >
                                                {allImages.map((image, index) => (
                                                   <img
                                                        key={index}
                                                        className="w-full h-full object-cover flex-shrink-0 snap-center rounded-lg"
                                                        src={image}
                                                        alt={`${name} ${index + 1}`}
                                                        onError={(e: any) => {
                                                            e.target.src = img;
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                            {allImages.length > 1 && (
                                               <div className="pointer-events-none absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1.5">
                                                   {allImages.map((_, i) => (
                                                       <span
                                                           key={i}
                                                           className={`h-1.5 w-1.5 rounded-full transition-colors ${
                                                               i === currentImageIndex ? 'bg-white/90' : 'bg-white/50'
                                                           }`}
                                                       />
                                                   ))}
                                               </div>
                                           )}
                                            {/* Chevron indicateur (cliquable) */}
                                            <button
                                                onClick={goToNextMobile}
                                                className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1"
                                            >
                                                <MdChevronRight className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            {/* Image indicators - desktop only */}
                            {allImages.length > 1 && (
                                <div className="hidden sm:flex justify-center space-x-1 mt-2">
                                    {allImages.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                scrollToImage(index);
                                            }}
                                            className={`w-2 h-2 rounded-full transition-all duration-200 ${
                                                index === currentImageIndex
                                                    ? 'bg-toola-500'
                                                    : 'bg-gray-300 hover:bg-gray-400'
                                            }`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                        <h2
                            className={`mt-1 px-[10px] text-sm font-medium leading-6 text-black dark:text-white`}
                        >
                            {displayName(name)}
                        </h2>
                        <span
                            className={`px-[10px] text-sm font-medium leading-6 text-black dark:text-white`}
                        >
                            {!hidePricesOnShop && price && price.toFixed(2)}
                            {!hidePricesOnShop && ' € / '}
                            {unity}
                        </span>
                        {packagings && packagings.length > 0 && (
                            <div 
                                className="px-[10px] mt-1"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <select
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                    value={resolvedPackaging?.unity || ''}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        if (e.target.value === '') {
                                            onPackagingChange?.(null);
                                        } else {
                                            const packaging = effectivePackagings.find(
                                                (p: any) => p.unity === e.target.value
                                            );
                                            onPackagingChange?.(packaging);
                                        }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    {!hideBaseUnit && (
                                        <option value="">
                                            Conditionnement de base
                                        </option>
                                    )}
                                    {effectivePackagings.map((pkg: any, idx: number) => (
                                        <option key={idx} value={pkg.unity}>
                                            {pkg.unity}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {subUnitQuantity > 0 && subUnity && !selectedPackaging && (
                            <span
                                className={`px-[10px] text-xs leading-6 text-black dark:text-white`}
                            >
                                Prix au {subUnity}:{' '}
                                {(price / subUnitQuantity).toFixed(2)}
                                {' €'}
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="col-span-12 flex flex-col px-2">
                        <h2
                            className={`text-sm font-medium leading-6 text-[#1B2559] dark:text-white mb-2`}
                        >
                            {displayName(name)}
                        </h2>
                        {packagings && packagings.length > 0 && (
                            <div className="mb-3">
                                <label className="text-xs text-gray-600 mb-1 block">Conditionnement :</label>
                                <select
                                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={resolvedPackaging?.unity || ''}
                                    onChange={(e) => {
                                        if (e.target.value === '') {
                                            onPackagingChange?.(null);
                                        } else {
                                            const packaging = effectivePackagings.find(
                                                (p: any) => p.unity === e.target.value
                                            );
                                            onPackagingChange?.(packaging);
                                        }
                                    }}
                                >
                                    {!hideBaseUnit && (
                                        <option value="">
                                            {unity} - {price.toFixed(2)}€
                                        </option>
                                    )}
                                    {effectivePackagings.map((pkg: any, idx: number) => {
                                        const listPkgLabel = pkg.useReprocessedPrice && pkg.weight > 0
                                            ? `${pkg.unity} - ${(parseFloat(pkg.price) * pkg.weight).toFixed(2)}€/${unity}`
                                            : `${pkg.unity} (${pkg.weight} ${unity}) - ${pkg.price}€`;
                                        return (
                                            <option key={idx} value={pkg.unity}>
                                                {listPkgLabel}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}
                        <div className="flex items-center gap-[6px]">
                            <input
                                id={id}
                                value={newQuantity.toString()}
                                min={0}
                                onChange={(e) => updateQuantity(e.target.value)}
                                type="number"
                                className={`noscroll flex w-[59px] h-[40px] rounded-xl border bg-white/0 px-2 text-sm outline-none`}
                            />
                            <span className="text-gray-600 text-sm">
                                {(selectedPackaging?.billByBaseUnit || selectedPackaging?.useReprocessedPrice)
                                    ? unity
                                    : (selectedPackaging?.unity || unity)}
                            </span>
                        </div>
                        <div className="mt-5 my-[4px] flex gap-[10px] items-center">
                            <span className="bold text-black font-bold text-sm">
                                Total :
                            </span>
                            <span
                                className={`text-xl font-bold leading-6 text-[#0260CB] dark:text-white`}
                            >
                                {(() => {
                                    const pkg = selectedPackaging;
                                    const effectivePrice = pkg?.useReprocessedPrice && pkg?.weight > 0
                                        ? parseFloat(pkg.price) * pkg.weight
                                        : (pkg?.price || price);
                                    return (newQuantity * effectivePrice).toFixed(2);
                                })()}
                                {' €'}
                            </span>
                        </div>
                        <CreateButton
                            value={
                                !quantity
                                    ? 'Ajouter'
                                    : newQuantity > 0
                                      ? 'Modifier'
                                      : 'Supprimer'
                            }
                            extra={`mt-[5px] h-[34px] !rounded-full ${
                                (!quantity && newQuantity === 0) ||
                                quantity === newQuantity
                                    ? 'bg-[#32383D] hover:bg-[#32383D] active:bg-[#32383D] cursor-default'
                                    : newQuantity === 0 && 'bg-[#F87171]'
                            }`}
                            onClick={() => {
                                if ((!quantity && newQuantity > 0) || quantity)
                                    handleSubmit();
                            }}
                        />
                        <div
                            className="mt-5 flex items-center justify-start cursor-pointer"
                            onClick={() => changeToogleState('normal')}
                        >
                            <svg
                                className="w-5 h-5 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                            <p className="text-xs">Retour à la fiche</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProductCard;
